/**
 * VehicleMapSchematic.tsx
 * @version 1.0.0 — Schéma interactif VP / VU avec zones cliquables
 */
import React, { memo } from 'react'
import { Camera, CheckCircle2 } from 'lucide-react'
import { photoMemory } from '../store/photoMemoryStore'

import { PhotoSlot } from '../utils/constants'

// ── Positions des zones (% du conteneur) ─────────────────────────────────────

export type SlotPos = { x: number; y: number }

// ── SVG VP (berline arrondie, vue de dessus) ─────────────────────────────────

export const VPSvg = memo(() => (
  <svg viewBox="0 0 200 380" className="w-full h-full" style={{ filter: 'drop-shadow(0 6px 28px rgba(0,0,0,0.5))' }}>
    <defs>
      <linearGradient id="vpBody" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor="#c8cdd5" />
        <stop offset="35%" stopColor="#e2e6ea" />
        <stop offset="65%" stopColor="#e2e6ea" />
        <stop offset="100%" stopColor="#b8bdc5" />
      </linearGradient>
      <linearGradient id="vpGlass" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#1e3a5a" />
        <stop offset="100%" stopColor="#2d4a6a" />
      </linearGradient>
    </defs>
    {/* Ombre au sol */}
    <ellipse cx="100" cy="200" rx="72" ry="175" fill="rgba(0,0,0,0.08)" />
    {/* Carrosserie principale — forme arrondie */}
    <path d="M 100,18 C 148,18 168,40 170,65 L 174,110 L 176,280 C 176,330 160,360 100,362 C 40,360 24,330 24,280 L 26,110 L 30,65 C 32,40 52,18 100,18 Z"
      fill="url(#vpBody)" stroke="#9ca3af" strokeWidth="1.5" />
    {/* Ligne de ceinture (reflet carrosserie) */}
    <path d="M 36,100 C 36,100 50,95 100,95 C 150,95 164,100 164,100"
      stroke="#ffffff" strokeWidth="0.8" fill="none" opacity="0.4" />
    {/* Pare-brise (incurvé) */}
    <path d="M 52,95 C 52,82 65,72 100,72 C 135,72 148,82 148,95 L 145,118 C 145,120 135,122 100,122 C 65,122 55,120 55,118 Z"
      fill="url(#vpGlass)" stroke="#6b7280" strokeWidth="1" opacity="0.85" />
    {/* Toit */}
    <path d="M 56,125 C 56,122 70,118 100,118 C 130,118 144,122 144,125 L 144,210 C 144,214 130,218 100,218 C 70,218 56,214 56,210 Z"
      fill="#d0d4d8" stroke="#9ca3af" strokeWidth="0.8" />
    {/* Lunette arrière */}
    <path d="M 58,218 C 58,216 72,213 100,213 C 128,213 142,216 142,218 L 140,248 C 140,252 128,256 100,256 C 72,256 60,252 60,248 Z"
      fill="url(#vpGlass)" stroke="#6b7280" strokeWidth="1" opacity="0.8" />
    {/* Vitres latérales gauche */}
    <path d="M 36,118 L 54,105 L 54,210 L 36,220 Z" fill="url(#vpGlass)" opacity="0.6" rx="2" />
    {/* Vitres latérales droite */}
    <path d="M 164,118 L 146,105 L 146,210 L 164,220 Z" fill="url(#vpGlass)" opacity="0.6" />
    {/* Rétroviseur gauche */}
    <ellipse cx="26" cy="108" rx="8" ry="5" fill="#c8cdd5" stroke="#9ca3af" strokeWidth="1" />
    <ellipse cx="25" cy="108" rx="4" ry="3" fill="#1e3a5a" opacity="0.7" />
    {/* Rétroviseur droit */}
    <ellipse cx="174" cy="108" rx="8" ry="5" fill="#c8cdd5" stroke="#9ca3af" strokeWidth="1" />
    <ellipse cx="175" cy="108" rx="4" ry="3" fill="#1e3a5a" opacity="0.7" />
    {/* Roues */}
    {([[30,85],[30,275],[170,85],[170,275]] as [number,number][]).map(([cx,cy],i) => (
      <g key={i}>
        <ellipse cx={cx} cy={cy} rx={14} ry={20} fill="#1a1a2e" stroke="#444" strokeWidth="2" />
        <ellipse cx={cx} cy={cy} rx={7} ry={10} fill="#2a2a3e" stroke="#555" strokeWidth="1" />
      </g>
    ))}
    {/* Phares avant */}
    <ellipse cx="62" cy="42" rx="16" ry="10" fill="#fef9c3" opacity="0.8" />
    <ellipse cx="138" cy="42" rx="16" ry="10" fill="#fef9c3" opacity="0.8" />
    {/* Feux arrière */}
    <path d="M 42,340 Q 42,348 55,350" stroke="#ef4444" strokeWidth="5" fill="none" strokeLinecap="round" opacity="0.9" />
    <path d="M 158,340 Q 158,348 145,350" stroke="#ef4444" strokeWidth="5" fill="none" strokeLinecap="round" opacity="0.9" />
    {/* Calandre */}
    <path d="M 72,30 L 128,30" stroke="#6b7280" strokeWidth="2.5" strokeLinecap="round" opacity="0.6" />
    {/* Plaque arrière */}
    <rect x="78" y="348" width="44" height="8" rx="2" fill="#fef9c3" stroke="#ca8a04" strokeWidth="0.5" opacity="0.7" />
    {/* Reflet capot */}
    <path d="M 80,35 C 80,28 90,24 100,24 C 110,24 120,28 120,35" stroke="#ffffff" strokeWidth="1" fill="none" opacity="0.4" />
  </svg>
))
VPSvg.displayName = 'VPSvg'

// ── SVG VU (fourgon, vue de dessus) ──────────────────────────────────────────

export const VUSvg = memo(() => (
  <svg viewBox="0 0 200 480" className="w-full h-full" style={{ filter: 'drop-shadow(0 6px 28px rgba(0,0,0,0.5))' }}>
    <defs>
      <linearGradient id="vuCab" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor="#c8cdd5" />
        <stop offset="40%" stopColor="#e2e6ea" />
        <stop offset="60%" stopColor="#e2e6ea" />
        <stop offset="100%" stopColor="#b8bdc5" />
      </linearGradient>
      <linearGradient id="vuCargo" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor="#d0d4d8" />
        <stop offset="50%" stopColor="#e8ecf0" />
        <stop offset="100%" stopColor="#c8ccd0" />
      </linearGradient>
      <linearGradient id="vuGlass" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#1a2e44" />
        <stop offset="100%" stopColor="#2a3e54" />
      </linearGradient>
    </defs>
    {/* Ombre au sol */}
    <ellipse cx="100" cy="250" rx="75" ry="225" fill="rgba(0,0,0,0.06)" />

    {/* ─── CABINE (haut, ~30% du véhicule) ──────────────────────────── */}
    <path d="M 100,18 C 145,18 160,32 162,52 L 164,90 L 166,150 L 34,150 L 36,90 L 38,52 C 40,32 55,18 100,18 Z"
      fill="url(#vuCab)" stroke="#9ca3af" strokeWidth="1.5" />
    {/* Pare-brise large */}
    <path d="M 50,68 C 50,52 65,42 100,42 C 135,42 150,52 150,68 L 148,95 C 148,98 138,100 100,100 C 62,100 52,98 52,95 Z"
      fill="url(#vuGlass)" stroke="#6b7280" strokeWidth="1" opacity="0.85" />
    {/* Toit cabine */}
    <path d="M 54,103 C 54,100 70,97 100,97 C 130,97 146,100 146,103 L 146,142 C 146,145 130,147 100,147 C 70,147 54,145 54,142 Z"
      fill="#d8dce0" stroke="#b0b4b8" strokeWidth="0.8" />
    {/* Rétroviseur gauche */}
    <ellipse cx="28" cy="82" rx="9" ry="6" fill="#c0c4c8" stroke="#9ca3af" strokeWidth="1" />
    <ellipse cx="27" cy="82" rx="5" ry="3.5" fill="#1a2e44" opacity="0.7" />
    {/* Rétroviseur droit */}
    <ellipse cx="172" cy="82" rx="9" ry="6" fill="#c0c4c8" stroke="#9ca3af" strokeWidth="1" />
    <ellipse cx="173" cy="82" rx="5" ry="3.5" fill="#1a2e44" opacity="0.7" />
    {/* Poignées de porte cabine */}
    <rect x="33" y="120" width="8" height="3" rx="1.5" fill="#9ca3af" opacity="0.7" />
    <rect x="159" y="120" width="8" height="3" rx="1.5" fill="#9ca3af" opacity="0.7" />

    {/* ─── CAISSE CARGO (bas, ~65% du véhicule) ─────────────────────── */}
    <rect x="28" y="155" width="144" height="290" rx="4"
      fill="url(#vuCargo)" stroke="#9ca3af" strokeWidth="1.5" />
    {/* Stries verticales (tôle ondulée) */}
    {Array.from({ length: 14 }, (_, i) => 28 + 10 + i * 9.5).map((x, i) => (
      <line key={i} x1={x} y1="162" x2={x} y2="438" stroke="#b8bcc0" strokeWidth="0.8" opacity="0.5" />
    ))}
    {/* Séparation cabine / cargo */}
    <line x1="28" y1="155" x2="172" y2="155" stroke="#8a8e92" strokeWidth="2" />
    {/* Portes arrière — ligne centrale */}
    <line x1="100" y1="430" x2="100" y2="448" stroke="#8a8e92" strokeWidth="1.5" opacity="0.6" />
    {/* Charnières portes arrière */}
    <rect x="32" y="435" width="6" height="4" rx="1" fill="#8a8e92" opacity="0.5" />
    <rect x="162" y="435" width="6" height="4" rx="1" fill="#8a8e92" opacity="0.5" />
    {/* Pare-choc arrière */}
    <rect x="32" y="446" width="136" height="8" rx="3" fill="#b0b4b8" stroke="#9ca3af" strokeWidth="0.8" />

    {/* Roues avant */}
    {([[24,72],[176,72]] as [number,number][]).map(([cx,cy],i) => (
      <g key={`f${i}`}>
        <ellipse cx={cx} cy={cy} rx={14} ry={20} fill="#1a1a2e" stroke="#555" strokeWidth="2" />
        <ellipse cx={cx} cy={cy} rx={7} ry={10} fill="#2a2a3e" stroke="#666" strokeWidth="1" />
      </g>
    ))}
    {/* Roues arrière (doubles) */}
    {([[24,390],[176,390]] as [number,number][]).map(([cx,cy],i) => (
      <g key={`r${i}`}>
        <ellipse cx={cx} cy={cy} rx={16} ry={22} fill="#1a1a2e" stroke="#555" strokeWidth="2" />
        <ellipse cx={cx} cy={cy} rx={8} ry={11} fill="#2a2a3e" stroke="#666" strokeWidth="1" />
      </g>
    ))}
    {/* Phares avant */}
    <ellipse cx="58" cy="28" rx="14" ry="8" fill="#fef9c3" opacity="0.8" />
    <ellipse cx="142" cy="28" rx="14" ry="8" fill="#fef9c3" opacity="0.8" />
    {/* Feux arrière */}
    <rect x="32" y="440" width="20" height="6" rx="2" fill="#ef4444" opacity="0.9" />
    <rect x="148" y="440" width="20" height="6" rx="2" fill="#ef4444" opacity="0.9" />
    {/* Calandre */}
    <path d="M 72,22 L 128,22" stroke="#8a8e92" strokeWidth="2.5" strokeLinecap="round" opacity="0.5" />
    {/* Plaque arrière */}
    <rect x="78" y="450" width="44" height="7" rx="2" fill="#fef9c3" stroke="#ca8a04" strokeWidth="0.5" opacity="0.7" />
    {/* Reflet capot */}
    <path d="M 80,26 C 80,22 90,18 100,18 C 110,18 120,22 120,26" stroke="#f0f4f8" strokeWidth="0.8" fill="none" opacity="0.4" />
  </svg>
))
VUSvg.displayName = 'VUSvg'

// ── ZoneButton ────────────────────────────────────────────────────────────────

interface ZoneButtonProps {
  slotId:   string
  label:    string
  pos:      SlotPos
  done:     boolean
  thumbSrc: string | null
  onTap:    (slotId: string) => void
  onThumb?: (slotId: string) => void  // retour : voir comparaison
  isRetour: boolean
}

export const ZoneButton = memo(({
  slotId, label, pos, done, thumbSrc, onTap, onThumb, isRetour,
}: ZoneButtonProps) => {
  return (
    <div
      className="absolute flex flex-col items-center gap-0.5"
      style={{
        left:      `${pos.x}%`,
        top:       `${pos.y}%`,
        transform: 'translate(-50%, -50%)',
        zIndex:    10,
      }}
    >
      {thumbSrc ? (
        // ── Zone avec miniature (faite ou départ) ──────────────────────────
        <div className="flex flex-col items-center gap-0.5">
          <div className="relative">
            <button
              onClick={() => isRetour && onThumb ? onThumb(slotId) : onTap(slotId)}
              className={`w-10 h-10 rounded-lg overflow-hidden border-2 ${done ? 'border-green-400 shadow-lg shadow-green-900/50' : 'border-amber-400 shadow-lg shadow-amber-900/50'} active:scale-90 transition-transform`}
              style={{ touchAction: 'manipulation' }}
              aria-label={label}
            >
              <img src={thumbSrc} alt={label} className="w-full h-full object-cover" />
            </button>
            <div className={`absolute -top-1.5 -right-1.5 rounded-full p-0.5 shadow ${done ? 'bg-green-500' : 'bg-amber-500'}`}>
              {done ? <CheckCircle2 size={12} className="text-white" /> : <Camera size={12} className="text-white" />}
            </div>
          </div>
          <span className={`text-[9px] font-semibold text-center leading-tight whitespace-nowrap bg-black/60 px-1.5 py-0.5 rounded backdrop-blur-sm border border-white/10 mt-1 ${done ? 'text-green-400' : 'text-amber-400'}`}>
            {label}
          </span>
        </div>
      ) : (
        // ── Zone vide : bouton caméra animé ─────────────────────────────────
        <div className="flex flex-col items-center gap-1">
          <button
            onClick={() => onTap(slotId)}
            className="w-11 h-11 rounded-full bg-slate-800 border-2 border-white flex items-center justify-center active:scale-90 transition-transform shadow-[0_4px_12px_rgba(0,0,0,0.6)]"
            style={{
              touchAction: 'manipulation',
              animation: 'pulse-amber 2s ease-in-out infinite',
            }}
            aria-label={`Photographier : ${label}`}
          >
            <Camera size={20} className="text-amber-400" />
          </button>
          <span className="text-[10px] text-amber-300 font-bold text-center leading-tight whitespace-nowrap bg-black/60 px-1.5 py-0.5 rounded backdrop-blur-sm border border-white/10">
            {label}
          </span>
        </div>
      )}
    </div>
  )
})
ZoneButton.displayName = 'ZoneButton'

// ── VehicleMapView ────────────────────────────────────────────────────────────

interface VehicleMapViewProps {
  vehicleType: 'VP' | 'VU'
  slots:       PhotoSlot[]
  photoVersion: number
  onTapSlot:   (slotId: string) => void
  onThumbSlot: (slotId: string) => void
  isRetour:    boolean
}

export const VehicleMapView = memo(({
  vehicleType, slots, photoVersion: _pv, onTapSlot, onThumbSlot, isRetour,
}: VehicleMapViewProps) => {

  return (
    <div className="relative h-full max-h-full max-w-full flex-shrink-0" style={{ aspectRatio: vehicleType === 'VU' ? '3/4.5' : '3/4.2' }}>
      {/* SVG véhicule */}
      <div className="absolute inset-0">
        {vehicleType === 'VU' ? <VUSvg /> : <VPSvg />}
      </div>

      {/* Zones interactives */}
      {slots.map(slot => {
        const x = vehicleType === 'VP' ? slot.vp_x : slot.vu_x
        const y = vehicleType === 'VP' ? slot.vp_y : slot.vu_y
        
        // Si les coordonnées ne sont pas définies, on centre l'icône par défaut
        const pos: SlotPos = { x: x ?? 50, y: y ?? 50 }
        
        const photo   = photoMemory.getPhoto(slot.id)
        const done    = !!photo
        let src     = photo
          ? (typeof photo === 'string' ? photo : (photo as any).dataUrl ?? null)
          : null
          
        // En mode retour, si la zone n'est pas encore photographiée,
        // afficher la miniature de départ depuis photoMemory.departPhotos
        if (!done && isRetour) {
          const departPhoto = photoMemory.getDepartPhoto(slot.id)
          if (departPhoto) {
            src = (departPhoto as any).dataUrl ?? null
          }
        }
          
        return (
          <ZoneButton
            key={slot.id}
            slotId={slot.id}
            label={slot.label}
            pos={pos}
            done={done}
            thumbSrc={src}
            onTap={onTapSlot}
            onThumb={onThumbSlot}
            isRetour={isRetour}
          />
        )
      })}
    </div>
  )
})
VehicleMapView.displayName = 'VehicleMapView'
